import { useEffect, useMemo, useState } from "react";
import parse from "html-react-parser";

type LoaderProps = {
  duration?: number;
  color?: string;
  onComplete?: () => void;

  size?: number;
  strokeWidth?: number;
  subtitle?: string;
};

const Loader = ({
  duration = 3,
  color = "#59AFFF",
  onComplete,
  size = 200,
  strokeWidth = 12,
  subtitle,
}: LoaderProps) => {
  const [progress, setProgress] = useState(0);

  const radius = (size - strokeWidth) / 2;
  const circumference = 2 * Math.PI * radius;

  const dashOffset = useMemo(() => {
    const p = Math.max(0, Math.min(100, progress));
    return circumference * (1 - p / 100);
  }, [circumference, progress]);

  useEffect(() => {
    const durationMs = duration * 1000;
    let rafId = 0;
    let start = 0;
    let finished = false;

    const tick = (ts: number) => {
      if (!start) start = ts;

      const elapsed = ts - start;
      const p = Math.min(100, (elapsed / durationMs) * 100);

      setProgress(p);

      if (p < 100) {
        rafId = requestAnimationFrame(tick);
      } else if (!finished) {
        finished = true;
        // let the final 100% frame paint before calling complete
        requestAnimationFrame(() => onComplete?.());
      }
    };

    rafId = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(rafId);
  }, [duration, onComplete]);

  return (
    <div className="flex flex-col items-center justify-center text-center">
      <div className="relative" style={{ width: size, height: size }}>
        <svg
          width={size}
          height={size}
          viewBox={`0 0 ${size} ${size}`}
          className="block"
        >
          {/* track */}
          <circle
            cx={size / 2}
            cy={size / 2}
            r={radius}
            fill="none"
            stroke="#E5E7EB"
            strokeWidth={strokeWidth}
          />

          {/* progress */}
          <circle
            cx={size / 2}
            cy={size / 2}
            r={radius}
            fill="none"
            stroke={color}
            strokeWidth={strokeWidth}
            strokeLinecap="round"
            strokeDasharray={circumference}
            strokeDashoffset={dashOffset}
            transform={`rotate(-90 ${size / 2} ${size / 2})`}
          />
        </svg>

        <div className="absolute inset-0 flex flex-col items-center justify-center">
          <div className="text-black font-bold text-[60px] leading-[60px]">
            {Math.round(progress)}%
          </div>
        </div>
        {subtitle ? (
          <div className="mt-4 text-[24px] text-black">{parse(subtitle)}</div>
        ) : null}
      </div>
    </div>
  );
};

export default Loader;
